import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, finalize, map, of, shareReplay, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { unwrap } from '../http/unwrap';
import type { IResponse } from '../models/api-response.model';
import type {
  ChangePasswordRequest,
  ChangePasswordResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  FullUserProfile,
  LoginRequest,
  LoginResponse,
  ProvisionUserRequest,
  ProvisionUserResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  Role,
  Tokens,
} from '../models/auth.model';

import { TokenStore } from './token.store';

/**
 * Requests to these URLs never get an `Authorization` header — consumed by `auth.interceptor.ts`
 * at the point it decides whether to attach the token. Distinct from `SELF_HANDLED_401_URLS` below:
 * `/auth/change-password` is authenticated (needs the header) but still owns its own 401 (a wrong
 * `currentPassword` 401s, and that must not force-logout the caller) — one list can't serve both
 * purposes, which is exactly the bug this split fixes.
 */
export const NO_AUTH_HEADER_URLS = [
  '/auth/login',
  '/auth/refresh',
  '/auth/forgot-password',
  '/auth/reset-password',
] as const;

/**
 * Requests to these URLs own their own 401 handling — `auth.interceptor.ts` rethrows their 401s
 * untouched instead of forcing a logout. Every `NO_AUTH_HEADER_URLS` entry belongs here too (an
 * unauthenticated call can't hit the refresh-and-retry path anyway), plus `/auth/change-password`,
 * which does carry a token but must let its 401 (wrong `currentPassword`) render inline instead of
 * kicking the user to `/login`.
 */
export const SELF_HANDLED_401_URLS = [...NO_AUTH_HEADER_URLS, '/auth/change-password'] as const;

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly tokens = inject(TokenStore);

  private readonly _user = signal<FullUserProfile | null>(null);
  private readonly _booting = signal(false);

  readonly user = this._user.asReadonly();
  readonly booting = this._booting.asReadonly();
  readonly isAuthenticated = computed(() => !!this._user());
  readonly role = computed<Role | null>(() => this._user()?.role ?? null);
  readonly mustChangePassword = computed(() => this._user()?.mustChangePassword ?? false);

  readonly isAdminOrAbove = computed(() => {
    const r = this.role();
    return r === 'ADMIN' || r === 'SUPER_ADMIN';
  });
  readonly isSuperAdmin = computed(() => this.role() === 'SUPER_ADMIN');
  readonly isDesigner = computed(() => {
    const r = this.role();
    return r === 'DESIGNER';
  });
  readonly hasAccessToken = computed(() => !!this.tokens.accessToken());

  /**
   * The password just typed at login, held in memory only, and only while `mustChangePassword` is
   * `true` — `/set-password` needs it as `currentPassword` for `PATCH /auth/change-password`
   * without asking the user to retype a password they entered seconds ago. Never written to
   * storage. Cleared on a successful change and on logout only — *not* on navigation away, since
   * the shell header's logo link round-trips through `/` → `landingPathFor()` → the
   * `mustChangePassword` gate → back to `/set-password`, and clearing on that hop would strand the
   * user in the re-enter-your-password fallback for no reason. A hard refresh loses it anyway
   * (in-memory only), which is exactly the case that fallback exists for.
   */
  private readonly _pendingCurrentPassword = signal<string | null>(null);
  readonly hasPendingPassword = computed(() => this._pendingCurrentPassword() !== null);

  /** Only for `/set-password`'s forced-rotation form, to prefill `currentPassword` without
   * asking the user to retype it. Returns `null` whenever `hasPendingPassword()` is `false`. */
  getPendingCurrentPassword(): string | null {
    return this._pendingCurrentPassword();
  }

  private refresh$: Observable<Tokens> | null = null;
  private bootstrap$: Observable<FullUserProfile | null> | null = null;

  get accessToken(): string | null {
    return this.tokens.accessToken();
  }

  get refreshToken(): string | null {
    return this.tokens.refreshToken();
  }

  login(input: LoginRequest): Observable<LoginResponse> {
    return this.http
      .post<IResponse<LoginResponse>>(`${environment.apiBaseUrl}/auth/login`, input)
      .pipe(
        map((res) => {
          if (!res.result || !res.data) {
            // Empty when the backend gave no message — the login page then shows a localized
            // fallback rather than an English literal.
            throw new Error(res.statusMessage || '');
          }
          return res.data;
        }),
        tap((res) =>
          this.tokens.setTokens({
            accessToken: res.accessToken,
            refreshToken: res.refreshToken,
          }),
        ),
        tap((res) => {
          // Carry mustChangePassword through — dropping it here (as this used to) means the
          // rotation gate reads `undefined` right after login and only picks up `true` on a
          // later hard-refresh bootstrap, exactly inverted from when it needs to fire.
          this.onAuthenticated({
            userId: res.userId,
            email: res.email,
            role: res.role,
            fullName: res.fullName,
            mustChangePassword: res.mustChangePassword,
          });
          this._pendingCurrentPassword.set(res.mustChangePassword ? input.password : null);
        }),
      );
  }

  loadProfile(): Observable<FullUserProfile> {
    return this.http.get<IResponse<FullUserProfile>>(`${environment.apiBaseUrl}/auth/profile`).pipe(
      map((res) => {
        if (!res.result || !res.data) {
          throw new Error(res.statusMessage || '');
        }
        return res.data;
      }),
      tap((user) => this._user.set(user)),
    );
  }

  /**
   * Self-service change, any role (`PATCH /auth/change-password`, api_specification.md §2). Also
   * resolves `mustChangePassword` regardless of what triggered it. Invalidates the refresh token
   * server-side — every device is logged out — so callers must re-authenticate after this
   * succeeds; this method does not do that itself (see `set-password.page.ts`/
   * `change-password.page.ts`'s silent re-login).
   */
  changePassword(input: ChangePasswordRequest): Observable<ChangePasswordResponse> {
    return this.http
      .patch<IResponse<ChangePasswordResponse>>(
        `${environment.apiBaseUrl}/auth/change-password`,
        input,
      )
      .pipe(
        unwrap(),
        tap(() => this._pendingCurrentPassword.set(null)),
      );
  }

  /**
   * `POST /auth/forgot-password` (public). Always resolves the same response whether or not the
   * email is registered — enumeration-safe by design (api_specification.md §2), not a case to
   * special-case in the caller.
   */
  forgotPassword(input: ForgotPasswordRequest): Observable<ForgotPasswordResponse> {
    return this.http
      .post<IResponse<ForgotPasswordResponse>>(
        `${environment.apiBaseUrl}/auth/forgot-password`,
        input,
      )
      .pipe(unwrap());
  }

  /**
   * `POST /auth/reset-password` (public). Same invalidate-every-device side effect as
   * `changePassword`; issues no tokens, so the caller lands back on `/login`, not a re-login.
   */
  resetPassword(input: ResetPasswordRequest): Observable<ResetPasswordResponse> {
    return this.http
      .post<IResponse<ResetPasswordResponse>>(
        `${environment.apiBaseUrl}/auth/reset-password`,
        input,
      )
      .pipe(unwrap());
  }

  /**
   * `POST /auth/provision` (SuperAdmin, api_specification.md §2:251-285). Creates a Judge/Admin/
   * SuperAdmin account with a server-generated temporary password, returned exactly once in the
   * response — this method does not cache or persist it anywhere. Does not touch `this._user` or
   * `TokenStore` — the acting SuperAdmin's own session is unaffected.
   */
  provision(input: ProvisionUserRequest): Observable<ProvisionUserResponse> {
    return this.http
      .post<IResponse<ProvisionUserResponse>>(`${environment.apiBaseUrl}/auth/provision`, input)
      .pipe(unwrap());
  }

  /** Patches the in-memory user without a refetch — e.g. after a profile-name save. */
  patchUser(partial: Partial<FullUserProfile>): void {
    const current = this._user();
    if (!current) return;
    this._user.set({ ...current, ...partial });
  }

  /**
   * Single-flight refresh: concurrent callers share one refresh HTTP call.
   */
  refresh(): Observable<Tokens> {
    if (this.refresh$) {
      return this.refresh$;
    }
    const refreshToken = this.tokens.refreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token'));
    }

    const body = {
      refreshToken: refreshToken,
    } as const;

    this.refresh$ = this.http
      .post<IResponse<Tokens>>(`${environment.apiBaseUrl}/auth/refresh`, body)
      .pipe(
        map((res) => {
          if (!res.result || !res.data) {
            throw new Error(res.statusMessage || '');
          }
          return res.data;
        }),
        tap((tokens) => this.tokens.setTokens(tokens)),

        tap(() => {
          this.refresh$ = null;
        }),
        catchError((err) => {
          this.refresh$ = null;
          return throwError(() => err);
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

    return this.refresh$;
  }

  /**
   * Called on app start to rehydrate user if a token exists.
   * Single-flight: concurrent callers share one bootstrap HTTP call.
   */
  bootstrap(): Observable<FullUserProfile | null> {
    if (this.bootstrap$) {
      return this.bootstrap$;
    }

    if (!this.tokens.accessToken()) {
      return of(null);
    }

    this._booting.set(true);

    this.bootstrap$ = this.loadProfile().pipe(
      tap((user) => this.onAuthenticated(user)),
      tap(() => {
        this._booting.set(false);
        this.bootstrap$ = null;
      }),
      catchError(() => {
        this._booting.set(false);
        this.bootstrap$ = null;
        this.hardLogout();
        return of(null);
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    return this.bootstrap$;
  }

  /**
   * @param navigateToLogin redirect to /login after clearing session
   * @param isForce skip the server-side POST /auth/logout call. Used by the auth
   *   interceptor when the token is already invalid/expired, so calling the logout
   *   endpoint would itself 401. User-initiated logout leaves this false so the
   *   backend can invalidate the refresh token.
   */
  logout(navigateToLogin = true, isForce = false): void {
    // Clear both user and tokens synchronously before navigating.
    // Deferring token removal caused a race: publicGuard would see hasAccessToken()=true
    // while the user was already null, trigger bootstrap(), and redirect back to the app.
    const finish = () => {
      this._user.set(null);
      this.tokens.clear();
      this._pendingCurrentPassword.set(null);
      if (navigateToLogin) {
        void this.router.navigateByUrl('/login');
      }
    };

    if (isForce) {
      finish();
      return;
    }

    void this.http
      .post<IResponse<void>>(`${environment.apiBaseUrl}/auth/logout`, {})
      .pipe(
        catchError(() => of(null)),
        finalize(() => finish()),
      )
      .subscribe();
  }

  private hardLogout(): void {
    this.tokens.clear();
    this._user.set(null);
    this._pendingCurrentPassword.set(null);
  }

  private onAuthenticated(user: FullUserProfile): void {
    this._user.set(user);
  }
}
